import { onMessage } from "firebase/messaging";
import Cookies from "js-cookie";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../api";
import { messaging, registerToken } from "../api/fcm";
import {
  authEvents$,
  chatEvents$,
  closeAuthSocket,
  closeChatSocket,
  initAuthSocket,
  initChatSocket,
  SOCKET_STATUS,
  socketStatus$,
} from "../api/webSocketRx";
import { conversationType, MESSAGE_STATUS } from "../config/Constant";
import { useInfoUser } from "../store/UserStore";
import {
  openNotificationError,
  openNotificationWarn,
  verifiedRefreshToken,
} from "../utils/Utils";
import { pushNotification } from "../utils/UtilsNotification";

const AuthLayoutContext = createContext(null);
const REACT_APP_AUTHENTICATION_WEB =
  process.env.REACT_APP_AUTHENTICATION_WEB || "http://localhost:4005";

const CURRENT_WEB =
  process.env.REACT_APP_CURRENT_WEB || "https://winitech.com.vn:3007";

export const useAuthLayoutContext = () => useContext(AuthLayoutContext);

export const AuthLayoutProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    user,
    updateUser,
    updateLanguageMap,
    languageMap,
    updateStickerKeywords,
  } = useInfoUser();

  const [orgList, setOrgList] = useState([]);
  const [isTreeLoading, setIsTreeLoading] = useState(false);

  const [verified, setVerified] = useState(false);

  const [call, setCall] = useState(null);
  // start call with audio or video
  const [callingType, setCallingType] = useState(null);
  const callingWindowRef = useRef(null);

  const [connectionState, setConnectionState] = useState({
    status: SOCKET_STATUS.DISCONNECTED,
    retryCount: 0,
  });

  const fetchWaitingCall = async () => {
    if (location.pathname === "/calling") return;

    const waitingCall = await apiFactory.callingApi.getWaitingCall();

    if (waitingCall?.status === 200) {
      setCall(waitingCall?.data);
    }
  };

  const getMe = async () => {
    if (user) return;

    const me = await apiFactory.userApi.getMe();
    if (me?.status === 200) {
      updateUser(me?.data?.user);
      updateLanguageMap(me?.data?.languageMap);
      updateStickerKeywords(me?.data?.stickerKeywords);
      await registerToken(me?.data?.user?.userId);
      await fetchWaitingCall();
    } else {
      toast.error(me?.message);
      updateUser(null);
      updateLanguageMap(null);
      Cookies.remove("access_token");
      navigate("/login");
    }
  };

  const fetchOrganizations = async () => {
    setIsTreeLoading(true);
    const result = await apiFactory.organizationApi.search({ setting: true });

    if (result?.status !== 200) {
      openNotificationError(result?.message);
      return;
    }

    const orgs = result?.data?.map((org) => ({
      ...org,
      checked: false,
      indeterminate: false,
      fetched: true,
      isOpen: false,
      id: uuidv4(),
    }));

    //auto open root node
    for (let org of orgs) {
      if (org?.parentId) continue;
      org.isOpen = true;
    }

    setOrgList([...orgs]);
    setIsTreeLoading(false);
  };

  const getAccessToken = async () => {
    if (user) return;

    const result = await apiFactory.authApi.getAccessToken({
      refreshToken: Cookies.get("refresh_token"),
    });
    if (result?.status === 200) {
      Cookies.set("access_token", result?.data);
    }
  };

  const process = async () => {
    const redirectRefreshToken = searchParams.get("token");
    let isValidToken = false;

    if (redirectRefreshToken) {
      Cookies.set("refresh_token", redirectRefreshToken);
      isValidToken = true;
    } else {
      isValidToken = verifiedRefreshToken();
    }

    setVerified(isValidToken);

    if (!isValidToken) {
      closeChatSocket();
      closeAuthSocket();
      window.location.href =
        REACT_APP_AUTHENTICATION_WEB +
        "/login?callback=" +
        window.location.href;
      return;
    }

    await getAccessToken();

    initChatSocket();
    initAuthSocket();

    await getMe();

    if (location?.pathname === "/") {
      navigate("/conversation");
    } else if (redirectRefreshToken) {
      let finalRedirectUrl = window.location.href.replace(
        "?token=" + redirectRefreshToken,
        "",
      );

      finalRedirectUrl = finalRedirectUrl.replace(
        "&&token=" + redirectRefreshToken,
        "",
      );

      window.location.href = finalRedirectUrl;
    }

    await fetchOrganizations();
  };

  const processFirebase = () => {
    try {
      onMessage(messaging, (payload) => {
        const data = {
          announcer:
            payload?.data?.announcer && JSON.parse(payload?.data?.announcer),
          basicConversationInfo:
            payload?.data?.basicConversationInfo &&
            JSON.parse(payload?.data?.basicConversationInfo),
          message: payload?.data?.message && JSON.parse(payload?.data?.message),
          type: payload?.data?.type,
        };

        const message = data?.message;
        const isSendImage = message?.contentType === MESSAGE_STATUS.IMAGE;
        const isSendText = message?.contentType === MESSAGE_STATUS.TEXT;
        const isSendFile = message?.contentType === MESSAGE_STATUS.FILE;

        const isShareMessage = data?.type === MESSAGE_STATUS.SHARE_MESSAGE;
        const isReactionMessage =
          data?.type === MESSAGE_STATUS.REACTION_MESSAGE;

        const isPinnedMessage =
          message?.contentType === MESSAGE_STATUS.PINNED_MESSAGE;
        const isUnPinnedMessage =
          message?.contentType === MESSAGE_STATUS.UNPINNED_MESSAGE;
        const isPromotedMessage =
          message?.contentType === MESSAGE_STATUS.PROMOTED_MESSAGE;

        const isSendByMe = data?.announcer?.userId === user?.userId;

        if (!isSendByMe) {
          handleNotifications(
            data,
            isSendText,
            isSendFile,
            isSendImage,
            isShareMessage,
            isReactionMessage,
            isUnPinnedMessage,
            isPinnedMessage,
            isPromotedMessage,
          );
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  /*
   * check exist of camera or mic
   * */

  const checkDevices = async (mode) => {
    try {
      if (!user) return;

      /* permission to get devices */
      const constraints = {};
      if (mode === "audio") {
        constraints.audio = true;
      } else if (mode === "video") {
        constraints.video = true;
      } else {
        constraints.audio = true;
        constraints.video = true;
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      const devices = await navigator.mediaDevices.enumerateDevices();
      /* only get no use */
      stream?.getTracks()?.forEach((track) => track?.stop());

      const videoDevices = devices
        .filter((device) => device?.kind === "videoinput")
        .map((device) => ({
          deviceId: device.deviceId,
          kind: device.kind,
          label: device.label,
          groupId: device.groupId,
          value: device.deviceId,
        }));

      const audioDevices = devices
        .filter(
          (device) =>
            device.kind === "audioinput" &&
            device.deviceId !== "default" &&
            device.deviceId !== "communications",
        )
        .map((device) => ({
          deviceId: device.deviceId,
          kind: device.kind,
          label: device.label,
          groupId: device.groupId,
          value: device.deviceId,
        }));

      if (!audioDevices?.length && mode === "audio") {
        return false;
      }

      if (!videoDevices?.length && mode === "video") {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  };

  const openCallingWindow = (url) => {
    if (!callingWindowRef.current || callingWindowRef.current.closed) {
      callingWindowRef.current = window.open(
        url,
        "_blank",
        `resizable=yes,scrollbars=no, width=${window.screen.availWidth},height=${window.screen.availHeight}`,
      );
    } else {
      callingWindowRef.current.location.href = url;
      callingWindowRef.current.focus();
    }
  };

  const openCall = async (memberIds, mode, conversationId) => {
    if (!(await checkDevices(mode))) {
      if (mode === "audio") openNotificationWarn("Your pc missed mic");

      if (mode === "video") openNotificationWarn("Your pc missed camera");

      return;
    }

    const result = await apiFactory.callingApi.createRoom({
      memberIds,
      conversationId,
    });

    setCallingType(null);

    if (!result?.data) {
      openNotificationWarn(
        "Everybody are busy! Let call again after few minutes",
      );
    } else if (mode === "audio") {
      openCallingWindow(
        CURRENT_WEB +
          "/calling?room_id=" +
          result?.data?.roomId +
          "&mode=" +
          mode +
          "&isSender=true",
      );
    } else {
      openCallingWindow(
        CURRENT_WEB +
          "/calling?room_id=" +
          result?.data?.roomId +
          "&isSender=true",
      );
    }
  };

  const handleNotifications = (
    data,
    isSendText,
    isSendFile,
    isSendImage,
    isShareMessage,
    isReactionMessage,
    isUnPinnedMessage,
    isPinnedMessage,
    isPromotedMessage,
  ) => {
    const userName = data?.announcer?.userName;

    if (isReactionMessage) {
      if (
        data?.message?.senderId === user?.userId &&
        Object.values(data?.message?.respondent || {})?.[0] !== "CANCEL"
      ) {
        pushNotification(
          `${userName} ${
            languageMap?.["message.event.reaction"] ?? "has send a reaction"
          }`,
          data?.message?.conversationId || null,
          languageMap,
        );
      }
    } else if (isShareMessage) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.shareMessage"] ?? "has shared a message."
        }`,
        data?.basicConversationInfos?.[0]?.conversationId || null,
        languageMap,
      );
    } else if (isUnPinnedMessage) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.isUnPinnedMessage"] ??
          "has unpinned a message."
        }`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    } else if (isPinnedMessage) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.isPinnedMessage"] ??
          "has pinned a message."
        }`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    } else if (isPromotedMessage) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.isPromotedMessage"] ??
          "has promoted a message."
        }`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    } else if (
      data?.basicConversationInfo?.type === conversationType.NOTIFICATION
    ) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.sendMessageNotification"] ??
          "sent you a message notification"
        }`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    } else if (isSendText) {
      switch (data?.basicConversationInfo?.type) {
        case conversationType.GROUP:
          return pushNotification(
            `${userName} ${
              languageMap?.["message.event.sendMessageConversation"] ??
              "has send a message conversation"
            }`,
            data?.basicConversationInfo?.conversationId || null,
            languageMap,
          );
        default:
          return pushNotification(
            `${userName} ${
              languageMap?.["message.event.sendMessage"] ??
              "sent you the message"
            }`,
            data?.basicConversationInfo?.conversationId || null,
            languageMap,
          );
      }
    } else if (isSendFile) {
      pushNotification(
        `${userName} ${languageMap?.["message.event.sendFile"] ?? "Attachments"}`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    } else if (isSendImage) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.sendImage"] ?? "sent you a photo"
        }`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    }
  };

  const getMember = (memberId) => {
    const member = orgList?.find((org) => org?.userCode === memberId);

    return {
      ...member,
      userId: memberId,
    };
  };

  useEffect(() => {
    process();
  }, []);

  useEffect(() => {
    const sub = socketStatus$.subscribe(async (s) => {
      if (s?.scope === "chat") {
        setConnectionState({
          status: s.status,
          retryCount: s.retryCount ?? 0,
        });

        if (s.status === "CONNECTED") {
          await fetchWaitingCall();
        }
      }
    });

    return () => sub.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    const sub = chatEvents$.subscribe((data) => {
      if (!data) return;

      switch (data?.type) {
        case "RINGING":
          setCall(data?.call);
          break;
        case "CLOSED_ROOM":
          if (data?.call?.roomId === call?.roomId) {
            setCall(null);
          }
          break;
        case "REJECTED":
          if (data?.call?.announcer?.userId === user?.userId) {
            setCall(null);
          }
          break;
        case "PRODUCED_VIDEO":
        case "PRODUCED_AUDIO":
          if (location.pathname === "/calling") return;

          if (data?.call?.roomId === call?.roomId) {
            setCall(null);
          }
          break;
        default:
          break;
      }
    });

    return () => sub.unsubscribe();
  }, [user, call]);

  useEffect(() => {
    if (!user) return;

    const sub = authEvents$.subscribe(async (data) => {
      if (!data) return;

      if (data?.type === "UPDATE_ORGANIZATION") {
        await fetchOrganizations();
      }
    });

    return () => sub.unsubscribe();
  }, [user]);

  useEffect(() => {
    if (user) processFirebase();
  }, [user]);

  useEffect(() => {
    window.onbeforeunload = function () {
      closeChatSocket();
      closeAuthSocket();
    };
  }, []);

  const values = useMemo(
    () => ({
      verified,
      setVerified,
      connectionState,
      orgList,
      setOrgList,
      fetchOrganizations,
      isTreeLoading,
      call,
      setCall,
      openCall,
      checkDevices,
      callingType,
      setCallingType,
      getMember,
      openCallingWindow,
    }),
    [verified, connectionState, orgList, isTreeLoading, call, callingType],
  );

  return (
    <AuthLayoutContext.Provider value={values}>
      {children}
    </AuthLayoutContext.Provider>
  );
};
